import React from 'react';
import _ from 'lodash';
import { Flex } from 'components/base/layout-components';
import {
  UnableToFindRepositories,
  LoadingTemplate,
  LimitExceeded,
  Subtitle
} from './RepositoriesModalV2/repositories-modal.components';
import {
  findRelevantSensorAuthorization,
  getActiveRepositories,
  shouldShowNoReposTemplate,
  inviteYourAdmin,
  talkToUs,
  findAndDeleteSensor,
  NO_GIT_ORGS_ERROR
} from './RepositoriesModalV2/repositories-modal.service';
import RepositoriesModalErrorHandler from './RepositoriesModalV2/RepositoriesModalErrorHandler';
import RepositoriesModalHeader from './RepositoriesModalV2/RepositoriesModalHeader';
import useRepositoriesModal from './RepositoriesModalV2/useRepositoriesModal';
import RepositoriesModalContent from './RepositoriesModalV2/RepositoriesModalContent';
import RepositoriesModalFooter from './RepositoriesModalV2/RepositoriesModalFooter';
import { isTeamByLicense } from 'common/utils/utils';
import { getProviderConfig } from 'common/utils/provider.utils';
import { MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING } from './RepositoriesModalV2/repositories.modal.consts';

const RepositoriesModalV2 = props => {
  const {
    license,
    gitIntegrations,
    sensor_authorization_id,
    deleteSensorAuthorization,
    toggleSnackBar,
    account,
    openReAuthModal,
    isGithubFetchAllRepoTypesFF,
    isConnectGitStreamFlow,
    repositories,
    createRepositories,
    onSubmitCb = _.noop,
    onRemoveMessage = _.noop,
    newRepositories = []
  } = props;
  const {
    sensor_name,
    base_url = '',
    is_pat: isPat
  } = findRelevantSensorAuthorization(
    gitIntegrations,
    sensor_authorization_id
  ) || {};

  const searchCharsLimit = 2;
  const providerConfig = getProviderConfig({ provider: sensor_name });
  const azureOrganizationName = providerConfig.organizationFromBaseUrl
    ? providerConfig.organizationFromBaseUrl(base_url)
    : null;

  const {
    onSubmit,
    setTerm,
    setLoading,
    setSelected,
    orgRepositories,
    loading,
    saving,
    error,
    numOfSelectableRepos,
    numOfFreeSlots,
    selected,
    term,
    isOnboarding,
    rowsPerPage,
    page,
    setPage,
    total,
    pageParams,
    setPageParams,
    orgs,
    selectedOrg,
    setSelectedOrg,
    isSinglePage,
    unselectedForNewRepos,
    setUnselectedForNewRepos,
    isBitbucket,
    hasNoActiveRepos
  } = useRepositoriesModal({
    sensor_authorization_id,
    repositories,
    license,
    isGithubFetchAllRepoTypesFF,
    account,
    createRepositories,
    onSubmitCb,
    onRemoveMessage,
    newRepositories,
    providerConfig,
    sensor_name,
    discoveredRepositories: repositories,
    searchCharsLimit,
    search: isConnectGitStreamFlow ? 'cm' : '',
    isPat,
    isConnectGitStreamFlow
  });
  if (!sensor_name || !gitIntegrations.length) {
    return null;
  }
  const showNoRepos =
    isSinglePage &&
    shouldShowNoReposTemplate(getActiveRepositories(orgRepositories)) &&
    isGithubFetchAllRepoTypesFF;

  const isTeamLicense = isTeamByLicense(license);

  const title = isConnectGitStreamFlow
    ? 'Connect the cm repo'
    : isOnboarding && !isTeamLicense
    ? 'Select Repositories'
    : 'Select repositories for your organization';

  const maxRepos =
    isOnboarding && isTeamLicense
      ? MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING
      : _.get(license, 'maxRepos', 0);
  const hasNoGitOrgs = error === NO_GIT_ORGS_ERROR;
  if (error && !hasNoGitOrgs) {
    return (
      <RepositoriesModalErrorHandler
        error={error}
        gitIntegrations={gitIntegrations}
        sensor_authorization_id={sensor_authorization_id}
        deleteSensorAuthorization={deleteSensorAuthorization}
        isOnboarding={isOnboarding}
        openReAuthModal={openReAuthModal}
      />
    );
  }

  if (!!loading) {
    return (
      <Flex direction='column' height='100%'>
        <LoadingTemplate text='Discovering new repositories...' />
      </Flex>
    );
  }

  if (numOfSelectableRepos <= 0) {
    return (
      <Flex direction='column' height='100%'>
        <LimitExceeded maxRepos={license.maxRepos} />
      </Flex>
    );
  }

  return showNoRepos && hasNoActiveRepos ? (
    <Flex
      direction='column'
      {...(isOnboarding ? {} : { justify: 'center', items: 'center' })}
      height='100%'
    >
      <UnableToFindRepositories
        provider={sensor_name}
        withTitleAndBoxes={isOnboarding}
        isOnboarding={isOnboarding}
        isTeamLicense={isTeamLicense}
        accessTokenCb={() =>
          findAndDeleteSensor({
            isOnboarding,
            gitIntegrations,
            sensor_authorization_id,
            deleteSensorAuthorization
          })
        }
        inviteYourAdminCb={() => inviteYourAdmin(toggleSnackBar, account)}
        talkToUsCb={talkToUs}
      />
    </Flex>
  ) : (
    <Flex direction='column' height='100%'>
      <Flex direction='column' pl='medium' pr='medium' height='100%'>
        <RepositoriesModalHeader
          sensor_name={sensor_name}
          title={title}
          subtitle={
            <Subtitle
              isOnboarding={isOnboarding}
              license={license}
              isConnectGitStreamFlow={isConnectGitStreamFlow}
            />
          }
          term={term}
          setTerm={setTerm}
          orgs={orgs}
          selectedOrg={selectedOrg}
          setSelectedOrg={setSelectedOrg}
          setPageParams={setPageParams}
          setLoading={setLoading}
          setPage={setPage}
          searchCharsLimit={searchCharsLimit}
        />
        <RepositoriesModalContent
          numOfFreeSlots={numOfFreeSlots}
          providerConfig={providerConfig}
          setSelected={setSelected}
          orgRepositories={orgRepositories}
          selected={selected}
          isOnboarding={isOnboarding}
          azureOrganizationName={azureOrganizationName}
          selectedOrg={selectedOrg}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          total={total}
          pageParams={pageParams}
          setPageParams={setPageParams}
          setLoading={setLoading}
          term={term}
          unselectedForNewRepos={unselectedForNewRepos}
          setUnselectedForNewRepos={setUnselectedForNewRepos}
          isGithubFetchAllRepoTypesFF={isGithubFetchAllRepoTypesFF}
          isBitbucket={isBitbucket}
          hasNoActiveRepos={hasNoActiveRepos}
          hasNoGitOrgs={hasNoGitOrgs}
        />
      </Flex>
      <RepositoriesModalFooter
        numOfFreeSlots={numOfFreeSlots}
        isOnboarding={isOnboarding}
        gitIntegrations={gitIntegrations}
        sensor_authorization_id={sensor_authorization_id}
        deleteSensorAuthorization={deleteSensorAuthorization}
        toggleSnackBar={toggleSnackBar}
        account={account}
        selected={selected}
        maxRepos={maxRepos}
        saving={saving}
        onSubmit={onSubmit}
        isTeamLicense={isTeamLicense}
        pageParams={pageParams}
        isConnectGitStreamFlow={isConnectGitStreamFlow}
      />
    </Flex>
  );
};

export default RepositoriesModalV2;
